import { ConfigService } from "@modules/core/services/configService"
import { type Service } from "@modules/core/services/serviceType"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { type ILocalStorage } from "@modules/storage/services/ILocalStorage"
import { LoggerType } from "@modules/utils/loggerUtils"
import { flatMapDeep, isFunction, isObject } from "lodash"
import { type Unsubscriber, WritableObservable } from "micro-observables"

export type LocalStorageConvertOptions<T extends object | null> = {
  convert?: {
    toStorage: (from: T) => string
    fromStorage: (to: string) => T
  }
}

export type LocalStorageOptions<T extends object | null> = LocalStorageConvertOptions<T> & {
  version?: number
  /**
   * Whether to synchronize this key across browser tabs/windows.
   * When enabled, changes to this key in one tab will automatically update the observable in other tabs.
   * Defaults to false.
   */
  syncAcrossTabs?: boolean
}

type StorageListener<T extends object | null> = {
  listener: (newValue: T | null) => void
  options?: LocalStorageOptions<T>
  defaultValue: T | (() => T)
}

export class LocalStorageService implements Service {
  private storageListeners = new Map<string, Set<StorageListener<any>>>()
  private isUpdatingFromStorage = false

  constructor(private _storage: ILocalStorage) {
    // Only on DEV, assert if there are a duplicated keys(to be sure to not use an existing key used to another thing)
    if (ConfigService.devMode) {
      const allKeys = flatMapDeep(LocalStorageKeys, (it) => (isObject(it) ? Object.values(it) : it))
      const duplicatedValues = allKeys.filter((value, index, acc) => acc.indexOf(value) !== index)
      if (duplicatedValues.length > 0) {
        console.error(LoggerType.LocalStorage + `duplicated storage keys: '${duplicatedValues.join(", ")}'`)
      }
    }

    // Listen to storage events from other tabs/windows
    if (typeof window !== "undefined") {
      window.addEventListener("storage", this.handleStorageEvent.bind(this))
    }
  }

  private handleStorageEvent(event: StorageEvent) {
    if (!event.key) return

    const listeners = this.storageListeners.get(event.key)
    if (!listeners || listeners.size === 0) return

    // Parse the wrapper from localStorage once
    // If newValue is null, the key was removed (e.g., during logout/clear)
    let rawDataString: string | null = null

    if (event.newValue !== null) {
      try {
        const parsed = JSON.parse(event.newValue)
        if (parsed && typeof parsed === "object" && "_version" in parsed && "data" in parsed) {
          rawDataString = parsed.data // Keep as string for conversion
        } else {
          // Fallback for non-standard format
          rawDataString = event.newValue
        }
      } catch (e) {
        console.error(LoggerType.LocalStorage + `Failed to parse storage event value for key ${event.key}`, e)
        return
      }
    }
    // If rawDataString is null, it means the key was removed (logout scenario)

    // Notify all listeners for this key (parse once per listener if needed)
    this.isUpdatingFromStorage = true
    listeners.forEach(({ listener, options, defaultValue }) => {
      try {
        let newValue: any = null
        if (rawDataString !== null) {
          if (options?.convert) {
            // Conversion expects a string
            newValue = options.convert.fromStorage(rawDataString)
          } else {
            // Parse the string value once
            newValue = JSON.parse(rawDataString)
          }
        }
        // If newValue is null, use default value
        const finalValue = newValue ?? (isFunction(defaultValue) ? defaultValue() : defaultValue)
        listener(finalValue)
      } catch (e) {
        console.error(LoggerType.LocalStorage + `Error in storage listener for key ${event.key}`, e)
      }
    })
    this.isUpdatingFromStorage = false
  }

  init(): Promise<void> {
    return Promise.resolve()
  }

  connect<T extends object | null>(
    observale: WritableObservable<T>,
    key: string,
    defaultValue: T | (() => T),
    options?: LocalStorageOptions<T>,
  ): Unsubscriber {
    observale.set(this.load(key, defaultValue, options))

    // Subscribe to observable changes and save to localStorage
    const unsubscribeObservable = observale.subscribe((value) => {
      // Only save if the change is not coming from a storage event (to avoid loops)
      if (!this.isUpdatingFromStorage) {
        this.save(key, value, options)
      }
    })

    // Register listener for storage events from other tabs (if syncAcrossTabs is enabled)
    const syncAcrossTabs = options?.syncAcrossTabs === true // Default to false
    let storageListener: StorageListener<T> | null = null

    if (syncAcrossTabs) {
      if (!this.storageListeners.has(key)) {
        this.storageListeners.set(key, new Set())
      }

      storageListener = {
        listener: (newValue: T | null) => {
          // Convert null to default value before setting
          const finalValue = newValue ?? (isFunction(defaultValue) ? defaultValue() : defaultValue)
          observale.set(finalValue)
        },
        options,
        defaultValue,
      }

      this.storageListeners.get(key)!.add(storageListener)
    }

    // Return cleanup function
    return () => {
      unsubscribeObservable()
      if (storageListener) {
        const listeners = this.storageListeners.get(key)
        if (listeners) {
          listeners.delete(storageListener!)
          if (listeners.size === 0) {
            this.storageListeners.delete(key)
          }
        }
      }
    }
  }

  load<T extends object | null>(key: string, defaultValue: T | (() => T), options?: LocalStorageOptions<T>): T {
    const getDefaultValue = () => {
      return isFunction(defaultValue) ? (defaultValue() as T) : defaultValue
    }

    function getConvertedValue(value: string): T {
      if (options?.convert) {
        return options.convert.fromStorage(value)
      } else {
        return JSON.parse(value) as T
      }
    }

    const debugInfo: { [key: string]: string | number | boolean } = {}
    try {
      const result = this._storage.load(key)

      debugInfo.hasDefaultValueFunc = isFunction(defaultValue)
      debugInfo.defaultValue = JSON.stringify(getDefaultValue())
      debugInfo.options = JSON.stringify(options)
      debugInfo.result = JSON.stringify(result)
      debugInfo.currentVersion = options?.version ?? 1

      if (result === null || result === undefined) {
        debugInfo.state = "null or undefined"
        return getDefaultValue()
      } else if (!isObject(result) || !("_version" in result) || !("data" in result)) {
        debugInfo.state = "bad format"
        if (ConfigService.devMode) {
          console.warn(LoggerType.LocalStorage + `load ${key} with bad format ${JSON.stringify(result)}`)
        } else {
          console.warn(LoggerType.LocalStorage + `load ${key} with bad format`)
        }
        return getDefaultValue()
      } else if (result._version !== (options?.version ?? 1)) {
        debugInfo.state = "migration"
        console.log(
          LoggerType.LocalStorage +
            `load ${key} with reset migration from ${result._version} to ${options?.version ?? 1}`,
        )
        return getDefaultValue()
      } else {
        debugInfo.state = "normal"
        return getConvertedValue(result.data)
      }
    } catch (e) {
      if (ConfigService.devMode) {
        console.error(LoggerType.LocalStorage + `load ${key} failed ${JSON.stringify(debugInfo)}`, e)
      } else {
        console.error(LoggerType.LocalStorage + `load ${key} failed`, e)
      }
      return getDefaultValue()
    }
  }

  save<T extends object | null>(key: string, value: T, options?: LocalStorageOptions<T>): void {
    try {
      const convertedValue = options?.convert ? options.convert.toStorage(value) : JSON.stringify(value)

      this._storage.save(key, {
        _version: options?.version ?? 1,
        data: convertedValue,
      })
    } catch (e) {
      if (ConfigService.config.ENV !== "prod") {
        console.error(LoggerType.LocalStorage + `save ${key} failed ${JSON.stringify(value)}`, e)
      } else {
        console.error(LoggerType.LocalStorage + `save ${key} failed`, e)
      }
    }
  }

  remove(key: string) {
    this._storage.remove(key)
  }

  clear() {
    const allKeys = flatMapDeep(LocalStorageKeys, (it) => (isObject(it) ? Object.values(it) : it))
    this._storage.multiRemove(allKeys)
  }
}
